import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutGrid,
  Building2,
  Users,
  Settings,
  LogOut,
  Moon,
  Sun,
  Menu,
} from "lucide-react";

const ROLE_LABELS = {
  superadmin: "Super Administrador",
  admin: "Administrador",
  editor: "Editor",
  viewer: "Visualizador",
};

const NAV = [
  { to: "/consulta", label: "Consulta", icon: LayoutGrid, roles: ["superadmin", "admin", "editor", "viewer"] },
  { to: "/empresas", label: "Empresas", icon: Building2, roles: ["superadmin"] },
  { to: "/usuarios", label: "Usuarios", icon: Users, roles: ["superadmin", "admin"] },
  { to: "/configuracion", label: "Configuración API", icon: Settings, roles: ["superadmin", "admin"] },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV.filter((n) => n.roles.includes(user.role));
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Overlay (mobile) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={closeMobile} data-testid="sidebar-overlay" />
      )}

      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 shrink-0 border-r border-border bg-card flex flex-col transform transition-transform duration-200 lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="h-16 flex items-center gap-2 px-6 border-b border-border">
          <img src="/zae-logo.png" alt="ZAE Clientes" className="h-8 w-8 rounded-sm object-contain" />
          <div className="font-heading font-bold tracking-tight text-lg leading-none">Clientes Corporativos Tomza</div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={closeMobile}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <Button
            variant="outline"
            className="w-full justify-start gap-2 rounded-sm mb-1"
            onClick={() => setDark((d) => !d)}
            data-testid="theme-toggle"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {dark ? "Modo claro" : "Modo oscuro"}
          </Button>
          <div className="px-3 py-2">
            <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user.name}</div>
            <div className="label-caps mt-0.5">{ROLE_LABELS[user.role]}</div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start gap-2 rounded-sm mt-1"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" />
            Cerrar sesión
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar (mobile/tablet) */}
        <header className="lg:hidden sticky top-0 z-30 flex items-center gap-3 h-14 px-4 border-b border-border bg-card">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 rounded-sm hover:bg-muted"
            aria-label="Abrir menú"
            data-testid="sidebar-toggle"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/zae-logo.png" alt="ZAE Clientes" className="h-7 w-7 rounded-sm object-contain" />
            <span className="font-heading font-bold tracking-tight text-base leading-none">Clientes Corporativos Tomza</span>
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
};
